import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Search, Calendar, Package, TrendingUp, Zap, AlertCircle, Eye } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CreateDeal from "./CreateDeal";
import { useTheme } from "components/Theme/ThemeProvider";
import { summaryApi } from "common";
import { authFetch } from "../../../helpers/authFetch";


// Import CreateDeal component (you'll need to import this from your actual file)
// import CreateDeal from './CreateDeal';

function DealStatsCard({ icon: Icon, label, value, colors, gradient }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05, y: -5 }}
      className="p-6 rounded-2xl relative overflow-hidden"
      style={{
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`
      }}
    >
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div
            className="p-3 rounded-xl"
            style={{ background: gradient }}
          >
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        <p className="text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
          {label}
        </p>
        <p className="text-3xl font-bold" style={{ color: colors.text }}>
          {value}
        </p>
      </div>
      
      {/* Animated Background */}
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full"
        style={{ background: gradient }}
      />
    </motion.div>
  );
}

function DealTableRow({ deal, colors, onToggle, onDelete, onEdit }) {
  const [showProducts, setShowProducts] = useState(false);
  
  const isActive = new Date(deal.endDate) > new Date() && deal.isActive;
  const totalProducts = deal.products.length;
  const totalSold = deal.products.reduce((sum, p) => sum + p.soldCount, 0);

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b hover:bg-opacity-50 transition-all"
        style={{ borderColor: colors.border }}
      >
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <Zap className="w-6 h-6" style={{ color: colors.primary }} />
            </div>
            <div>
              <p className="font-semibold" style={{ color: colors.text }}>
                {deal.dealName}
              </p>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                {deal.dealType.replace('_', ' ').toUpperCase()}
              </p>
            </div>
          </div>
        </td>

        <td className="p-4">
          <p className="text-sm line-clamp-2" style={{ color: colors.textSecondary }}>
            {deal.dealDescription}
          </p>
        </td>

        <td className="p-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold" style={{ color: colors.text }}>
              {totalProducts} Products
            </p>
            <p className="text-xs" style={{ color: colors.textSecondary }}>
              {totalSold} sold
            </p>
          </div>
        </td>

        <td className="p-4">
          <div className="space-y-1">
            <p className="text-xs font-semibold" style={{ color: colors.success }}>
              {new Date(deal.startDate).toLocaleDateString()}
            </p>
            <p className="text-xs font-semibold" style={{ color: colors.error }}>
              {new Date(deal.endDate).toLocaleDateString()}
            </p>
          </div>
        </td>

        <td className="p-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onToggle(deal._id)}
            className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: isActive ? `${colors.success}20` : `${colors.error}20`,
              color: isActive ? colors.success : colors.error
            }}
          >
            {isActive ? (
              <>
                <ToggleRight className="w-4 h-4" />
                Active
              </>
            ) : (
              <>
                <ToggleLeft className="w-4 h-4" />
                Inactive
              </>
            )}
          </motion.button>
        </td>

        <td className="p-4">
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowProducts(!showProducts)}
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}
              title="View Products"
            >
              <Eye className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onEdit(deal)}
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
              title="Edit Deal"
            >
              <Edit className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onDelete(deal._id)}
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${colors.error}20`, color: colors.error }}
              title="Delete Deal"
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
          </div>
        </td>
      </motion.tr>

      {/* Expandable Products Row */}
      <AnimatePresence>
        {showProducts && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <td colSpan="6" className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deal.products.map((product) => (
                  <div
                    key={product.productId._id}
                    className="flex gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                  >
                    <img
                      src={product.productId.productImage[0]}
                      alt={product.productId.productName}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold line-clamp-1" style={{ color: colors.text }}>
                        {product.productId.productName}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-bold" style={{ color: colors.primary }}>
                          ${product.dealPrice}
                        </span>
                        <span className="text-xs line-through" style={{ color: colors.textSecondary }}>
                          ${product.productId.productPrice}
                        </span>
                      </div>
                      {product.stockLimit && (
                        <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                          {product.soldCount}/{product.stockLimit} sold
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

function ManageDeals() {
  const { colors } = useTheme();
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    filterDeals();
  }, [searchQuery, filterStatus, deals]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await authFetch(summaryApi.getAllDeals.url, {
        method: summaryApi.getAllDeals.method        
      });
      const data = await response.json();
      
      if (data.success) {
        setDeals(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterDeals = () => {
    let filtered = deals;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(deal =>
        deal.dealName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.dealDescription.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus === "active") {
      filtered = filtered.filter(deal => 
        deal.isActive && new Date(deal.endDate) > new Date()
      );
    } else if (filterStatus === "expired") {
      filtered = filtered.filter(deal => 
        new Date(deal.endDate) <= new Date()
      );
    } else if (filterStatus === "inactive") {
      filtered = filtered.filter(deal => !deal.isActive);
    }

    setFilteredDeals(filtered);
  };

  const handleToggleDealStatus = async (dealId) => {
    try {
      const response = await authFetch(`${summaryApi.toggleDealStatus.url}/${dealId}`, {
        method: summaryApi.toggleDealStatus.method        
      });
      const data = await response.json();
      
      if (data.success) {
        fetchDeals();
      }
    } catch (error) {
      console.error("Error toggling deal status:", error);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm("Are you sure you want to delete this deal?")) {
      return;
    }

    try {
      const response = await authFetch(`${summaryApi.deleteDeal.url}/${dealId}`, {
        method: summaryApi.deleteDeal.method
        
      });
      const data = await response.json();
      
      if (data.success) {
        fetchDeals();
      }
    } catch (error) {
      console.error("Error deleting deal:", error);
    }
  };

  const handleEditDeal = (deal) => {
    // Implement edit functionality
    console.log("Edit deal:", deal);
  };

  // Calculate stats
  const activeDeals = deals.filter(d => d.isActive && new Date(d.endDate) > new Date()).length;
  const totalProducts = deals.reduce((sum, d) => sum + d.products.length, 0);
  const totalRevenue = deals.reduce((sum, d) => 
    sum + d.products.reduce((pSum, p) => pSum + (p.soldCount * p.dealPrice), 0), 0
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="p-6 rounded-full"
          style={{ backgroundColor: `${colors.primary}20` }}
        >
          <Zap className="w-12 h-12" style={{ color: colors.primary }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black flex items-center gap-3" style={{ color: colors.text }}>
              <Zap className="w-10 h-10" style={{ color: colors.primary }} />
              Manage Flash Deals
            </h1>
            <p className="mt-2" style={{ color: colors.textSecondary }}>
              Create and manage your flash sales and special offers
            </p>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
            }}
          >
            <Plus className="w-5 h-5" />
            Create Deal
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DealStatsCard
            icon={Zap}
            label="Active Deals"
            value={activeDeals}
            colors={colors}
            gradient={`linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`}
          />
          <DealStatsCard
            icon={Package}
            label="Total Products"
            value={totalProducts}
            colors={colors}
            gradient={`linear-gradient(135deg, ${colors.accent} 0%, ${colors.error} 100%)`}
          />
          <DealStatsCard
            icon={TrendingUp}
            label="Total Revenue"
            value={`₦${totalRevenue.toFixed(2)}`}
            colors={colors}
            gradient={`linear-gradient(135deg, ${colors.success} 0%, ${colors.primary} 100%)`}
          />
        </div>

        {/* Filters */}
        <div
          className="p-6 rounded-2xl space-y-4"
          style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: colors.textSecondary }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search deals..."
                className="w-full pl-12 pr-4 py-3 rounded-lg outline-none"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.text,
                  border: `2px solid ${colors.border}`
                }}
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              {["all", "active", "expired", "inactive"].map((status) => (
                <motion.button
                  key={status}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilterStatus(status)}
                  className="px-4 py-2 rounded-lg font-semibold text-sm capitalize transition-all"
                  style={{
                    backgroundColor: filterStatus === status ? colors.primary : colors.bg,
                    color: filterStatus === status ? "white" : colors.text,
                    border: `2px solid ${filterStatus === status ? colors.primary : colors.border}`
                  }}
                >
                  {status}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm" style={{ color: colors.textSecondary }}>
            <Calendar className="w-4 h-4" />
            Showing {filteredDeals.length} of {deals.length} deals
          </div>
        </div>

        {/* Deals Table */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
        >
          {filteredDeals.length === 0 ? (
            <div className="p-12 text-center space-y-4">
              <AlertCircle className="w-16 h-16 mx-auto" style={{ color: colors.textSecondary }} />
              <p className="text-xl font-semibold" style={{ color: colors.text }}>
                No deals found
              </p>
              <p style={{ color: colors.textSecondary }}>
                {searchQuery || filterStatus !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first deal to get started!"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: colors.bg }}>
                  <tr>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Deal Info
                    </th>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Description
                    </th>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Products
                    </th>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Duration
                    </th>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Status
                    </th>
                    <th className="p-4 text-left text-sm font-semibold" style={{ color: colors.text }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map((deal) => (
                    <DealTableRow
                      key={deal._id}
                      deal={deal}
                      colors={colors}
                      onToggle={handleToggleDealStatus}
                      onDelete={handleDeleteDeal}
                      onEdit={handleEditDeal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Deal Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50">
          {/* You'll need to import and use the CreateDeal component here */}
          <CreateDeal 
    onClose={() => setShowCreateModal(false)}
    fetchDeals={fetchDeals}
  />
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold mb-4">Create Deal</h3>
              <p className="text-gray-600 mb-6">
                Import the CreateDeal component to enable deal creation.
              </p>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageDeals;


// total revenue